import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../../../../components/Modal";
import Button from "../../../../../components/Button";
import { useCompanyDateFormat } from "../../../../../utils/hooks/company";
import { dateTimeFormat } from "../../../../../utils/helpers/date";
import { useTimeOffPlanVersionsApi } from "./useTimeOffPlanVersions";
import { getPendingVersion } from "./scheduledChange";

/**
 * Row action Edit for a plan that may have a scheduled change (a pending version).
 * Without one the form opens as usual. With one the user first chooses: edit the scheduled change
 * (the form gets the scheduled settings, see `scheduledFormValues`), discard it and edit the
 * current settings, or cancel. `scheduled` is the pending version being edited, for the plan the
 * form is open on (null otherwise).
 */
export const useScheduledChangeEdit = ({ goToEdit, mode, selectedId }) => {
    const { t } = useTranslation();
    const dateFormat = useCompanyDateFormat();
    const { fetchVersions, discardVersion } = useTimeOffPlanVersionsApi();
    const [choice, setChoice] = useState(null); // { planId, version } while the question is open
    const [editing, setEditing] = useState(null); // { planId, version } for the form
    const [checking, setChecking] = useState(false);
    const [discarding, setDiscarding] = useState(false);

    const edit = useCallback(async (planId) => {
        if (checking) {
            return;
        }
        setEditing(null);
        setChecking(true);
        let version = null;
        try {
            version = getPendingVersion(await fetchVersions(planId));
        } catch (err) {
            version = null;
        } finally {
            setChecking(false);
        }
        if (version) {
            setChoice({ planId, version });
        } else {
            goToEdit(planId);
        }
    }, [checking, fetchVersions, goToEdit]);

    const cancel = useCallback(() => setChoice(null), []);

    const editScheduled = useCallback(() => {
        setEditing(choice);
        setChoice(null);
        goToEdit(choice.planId);
    }, [choice, goToEdit]);

    const discardAndEdit = useCallback(async () => {
        setDiscarding(true);
        const discarded = await discardVersion(choice.planId, choice.version);
        setDiscarding(false);
        if (discarded) {
            setChoice(null);
            goToEdit(choice.planId);
        }
    }, [choice, discardVersion, goToEdit]);

    const date = choice ? dateTimeFormat(choice.version.startEffectiveDate, dateFormat) : "";

    const modal = (
        <Modal
            isOpen={!!choice}
            toggle={discarding ? undefined : cancel}
            centered
            title={<h3 className="mb-0">{t("time-off-plan-edit-scheduled-change-title")}</h3>}
            footer={
                <div className="d-flex flex-column w-100" style={{ gap: 8 }}>
                    <Button className="m-0" color="primary" onClick={editScheduled} disabled={discarding}>
                        {t("time-off-plan-edit-scheduled-change")}
                    </Button>
                    <Button className="m-0" color="danger" outline onClick={discardAndEdit} loading={discarding}>
                        {t("time-off-plan-discard-and-edit-current")}
                    </Button>
                    <Button className="m-0" color="light" onClick={cancel} disabled={discarding}>
                        {t("cancel")}
                    </Button>
                </div>
            }
        >
            <p className="mb-0">{t("time-off-plan-has-scheduled-change", { date })}</p>
        </Modal>
    );

    const scheduled = useMemo(
        () => (mode === "edit" && editing && editing.planId === selectedId ? editing.version : null),
        [mode, editing, selectedId]
    );

    return { edit, modal, scheduled };
};
